// Plain functions with type-only imports, so the server-side tests can use them too.
import type { Item, Where } from "../../src/web/types.ts";

export type LngLat = [number, number];

const lngLat = (where: Where | undefined): LngLat | null =>
  where?.lat !== undefined && where.lng !== undefined ? [where.lng, where.lat] : null;

// Where an item sits on the map: its place; for a move, halfway along it (or wherever it's known to
// start or end). None without coordinates.
export function point(item: Item): LngLat | null {
  if (item.type === "move" && !item.where) {
    const from = lngLat(item.from);
    const to = lngLat(item.to);
    if (from && to) return midway(from, to);
    return from ?? to;
  }
  return lngLat(item.where);
}

// The middle of the straight line the map draws between two points. The map is Web Mercator, so that's
// halfway in projected y, not in latitude (which would sit off the line on a long flight).
export function midway(a: LngLat, b: LngLat): LngLat {
  return alongLine(a, b, 0);
}

// A point on the straight line from a to b as the map draws it (Mercator): t from -1 (a) through 0 (the middle)
// to 1 (b). The same point at every zoom, as a journey's icon kept at one part of its line is.
export function alongLine(a: LngLat, b: LngLat, t: number): LngLat {
  const y = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
  const k = (1 + t) / 2;
  const at = y(a[1]) + (y(b[1]) - y(a[1])) * k;
  return [a[0] + (b[0] - a[0]) * k, (360 / Math.PI) * Math.atan(Math.exp(at)) - 90];
}

// Every coordinate an item covers, for framing the map: a move's both ends.
export function extent(item: Item): LngLat[] {
  const ends = [lngLat(item.from), lngLat(item.to)].filter((p): p is LngLat => p !== null);
  const at = point(item);
  return item.type === "move" && ends.length ? ends : at ? [at] : [];
}

// The map shows places, goals, stays and moves; tasks and info live elsewhere.
export const onMapTypes = new Set(["place", "goal", "stay", "move"]);

export function distanceM(a: LngLat, b: LngLat): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLng = (b[0] - a[0]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

// A circle of radius m around a point, as a GeoJSON ring.
export function circle(center: LngLat, m: number, steps = 72): LngLat[] {
  const ring: LngLat[] = [];
  const dLat = m / 111_320;
  const dLng = m / (111_320 * Math.max(Math.cos((center[1] * Math.PI) / 180), 0.01));
  for (let k = 0; k <= steps; k++) {
    const a = (2 * Math.PI * k) / steps;
    ring.push([center[0] + dLng * Math.cos(a), center[1] + dLat * Math.sin(a)]);
  }
  return ring;
}

export function bounds(points: LngLat[]): [LngLat, LngLat] | null {
  if (points.length === 0) return null;
  let [w, s] = points[0] as LngLat;
  let [e, n] = [w, s];
  for (const [lng, lat] of points) {
    w = Math.min(w, lng);
    e = Math.max(e, lng);
    s = Math.min(s, lat);
    n = Math.max(n, lat);
  }
  return [
    [w, s],
    [e, n],
  ];
}
